import { BaseComponent } from './../../../../common/components/base.component';
import { ActivatedRoute } from '@angular/router';
import { FavoriteCustomEvent } from './../../../shared/models/favorite.custom.event';
import { Component, ViewChild, ViewEncapsulation } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { takeUntil, delay } from 'rxjs';
import { frontendSettingsSelector } from 'src/app/modules/shared/ngrx.stores/frontend.settings/selectors';
import { IAppState } from 'src/app/modules/shared/ngrx.stores/frontend.settings/states';
import * as EventQueryCommandActions from 'src/app/modules/event-calendar/ngrx.stores/event.query.command/actions';
import { eventQueryCommandSelector } from 'src/app/modules/event-calendar/ngrx.stores/event.query.command/selectors';
import { Location } from '@angular/common';
import { VisitorService } from 'src/app/modules/shared/services/visitor.service';
import { AccordionComponent } from 'src/app/common/components/accordion/accordion.component';

@Component({
  selector: 'ig-multi-select-event-category-filter',
  templateUrl: './multi.select.event.category.filter.component.html',
  styleUrls: ['./multi.select.event.category.filter.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class MultiSelectEventCategoryFilterComponent extends BaseComponent {
  @ViewChild('categoryAccordion') categoryAccordion: AccordionComponent;

  categoryTitle = 'EVENT CATEGORIES';
  selectedCategoryIds: string[] = [];
  matchAllCategories = false;
  pendingCategoryIds: string[] = [];
  pendingMatchAll = false;
  isEditorChoice = false;
  customEventCategories: FavoriteCustomEvent[] = [];
  isNoneMode = false;
  isHideEditorChoice = true;
  editorChoiceText = '';

  // Embedded "locked base" category: always applied, ANDed with additional selections.
  lockedCategoryId = '';
  lockedCategory: FavoriteCustomEvent | null = null;

  private originLink = '';

  constructor(
    public activatedRoute: ActivatedRoute,
    private _store: Store<IAppState>,
    private _location: Location,
    public visitorService: VisitorService,
  ) {
    super({ activatedRoute });

    this._store
      .pipe(takeUntil(this.isDestroyed), select(frontendSettingsSelector))
      .subscribe((settings) => {
        if (!settings) return;
        this.customEventCategories = settings.customSettings.customEvents || [];
        this.isHideEditorChoice = settings.customSettings.isHideEditorChoice;
        this.editorChoiceText = settings.customSettings.customEditorChoice?.editorChoiceForEvent || "Editor's Choice";
        this.resolveLockedCategory();
      });

    // Restore state from store (e.g. after URL navigation)
    this._store
      .pipe(takeUntil(this.isDestroyed), delay(500), select(eventQueryCommandSelector))
      .subscribe((cmd) => {
        if (!cmd) return;

        let originLink = this._location.path().split('ig-custom-events=')[0];
        if (originLink.endsWith('?') || originLink.endsWith('&')) {
          originLink = originLink.slice(0, -1);
        }
        this.originLink = originLink;

        this.lockedCategoryId = cmd.lockedCategoryId || '';
        this.resolveLockedCategory();

        if (cmd.favoriteEventBtnIds) {
          if (cmd.favoriteEventBtnIds === 'none') {
            this.selectedCategoryIds = [];
            this.isNoneMode = true;
          } else {
            let ids = cmd.favoriteEventBtnIds.split(',').filter(Boolean);
            if (this.lockedCategoryId) {
              // The locked base is shown separately; keep only the additional selections here.
              ids = ids.filter((id) => id !== this.lockedCategoryId);
            }
            this.selectedCategoryIds = ids;
            this.isNoneMode = false;
          }
          this.matchAllCategories = this.lockedCategoryId ? true : !!cmd.matchAllCategories;
          this.isEditorChoice = false;
          this.updateTitle();
        } else if (cmd.favoriteEventBtnId) {
          // Single-select backward compat from URL
          this.selectedCategoryIds = [cmd.favoriteEventBtnId];
          this.matchAllCategories = false;
          this.isEditorChoice = false;
          this.updateTitle();
        } else if (cmd.choice) {
          // Editor's Choice only
          this.selectedCategoryIds = [];
          this.isEditorChoice = true;
          this.categoryTitle = this.editorChoiceText;
        } else {
          // Only reset if nothing is selected (avoid overwriting on unrelated cmd updates)
          if (!this.selectedCategoryIds.length && !this.isEditorChoice) {
            this.categoryTitle = 'EVENT CATEGORIES';
          }
        }
      });
  }

  /** Categories the visitor can toggle (the locked base is shown separately, not here). */
  get selectableCategories(): FavoriteCustomEvent[] {
    if (!this.lockedCategoryId) return this.customEventCategories;
    return this.customEventCategories.filter((c) => c._id !== this.lockedCategoryId);
  }

  /** "ALL" is checked only when every selectable category is pending-selected. */
  get pendingAll(): boolean {
    return (
      !this.lockedCategoryId &&
      this.selectableCategories.length > 0 &&
      this.selectableCategories.every((c) => this.pendingCategoryIds.includes(c._id))
    );
  }

  private resolveLockedCategory() {
    this.lockedCategory = this.lockedCategoryId
      ? this.customEventCategories.find((c) => c._id === this.lockedCategoryId) || null
      : null;
    if (this.lockedCategoryId) {
      this.updateTitle();
    }
  }

  syncPendingCategories() {
    // Default (nothing selected) => nothing checked, so an immediate CONFIRM shows all events.
    this.pendingCategoryIds = [...this.selectedCategoryIds];
    this.pendingMatchAll = this.matchAllCategories;
  }

  toggleCategory(categoryId: string) {
    const idx = this.pendingCategoryIds.indexOf(categoryId);
    if (idx >= 0) {
      this.pendingCategoryIds.splice(idx, 1);
    } else {
      this.pendingCategoryIds.push(categoryId);
    }
  }

  toggleAll() {
    if (this.pendingAll) {
      this.pendingCategoryIds = [];
    } else {
      this.pendingCategoryIds = this.selectableCategories.map((c) => c._id);
    }
  }

  toggleMatchAllCategories() {
    this.pendingMatchAll = !this.pendingMatchAll;
  }

  /**
   * Editor's Choice is a standalone action button: clicking it queries ONLY
   * editor's choice data (mutually exclusive with categories; ignores the
   * locked base — global editor's choice). Applies immediately and closes.
   */
  selectEditorChoiceOnly() {
    this.isEditorChoice = true;
    this.selectedCategoryIds = [];
    this.matchAllCategories = false;
    this.isNoneMode = false;
    this.categoryTitle = this.editorChoiceText;

    this._store.dispatch(EventQueryCommandActions.setIsChoice({ choice: true }));

    // Editor's Choice is category-agnostic — drop any category params from the URL.
    if (this.originLink) {
      this._location.go(this.originLink);
    }
    this.categoryAccordion?.closeMenu();
  }

  confirmCategorySelection() {
    this.isEditorChoice = false; // categories and Editor's Choice are mutually exclusive
    this.isNoneMode = false;

    // Expand "ALL" to the concrete selectable ids so selecting ALL filters to events
    // that have a custom category (union / matchAll) instead of clearing the filter.
    this.selectedCategoryIds = this.pendingAll
      ? this.selectableCategories.map((c) => c._id)
      : [...this.pendingCategoryIds];
    this.matchAllCategories = this.lockedCategoryId ? true : this.pendingMatchAll;

    // The locked base is always part of the query and forces AND semantics.
    const effectiveIds = this.lockedCategoryId
      ? [this.lockedCategoryId, ...this.selectedCategoryIds]
      : [...this.selectedCategoryIds];

    if (effectiveIds.length > 0) {
      this.updateTitle();
      this._store.dispatch(
        EventQueryCommandActions.setFavoriteEventBtnIds({
          favBtnIds: effectiveIds.join(','),
          matchAllCategories: this.matchAllCategories,
        })
      );
      this.updateUrl();
    } else {
      // Nothing selected — clear the category filter, show all events.
      this.categoryTitle = 'EVENT CATEGORIES';
      this._store.dispatch(
        EventQueryCommandActions.setFavoriteEventBtnIds({
          favBtnIds: '',
          matchAllCategories: false,
        })
      );
      if (this.originLink) {
        this._location.go(this.originLink);
      }
    }

    this.categoryAccordion?.closeMenu();
  }

  clearCategories() {
    if (this.lockedCategoryId) {
      // Locked mode: reset to "base only", never clear the locked base itself.
      this.selectedCategoryIds = [];
      this.matchAllCategories = true;
      this.isEditorChoice = false;
      this.isNoneMode = false;
      this.pendingCategoryIds = [];
      this.pendingMatchAll = true;
      this.updateTitle();

      this._store.dispatch(
        EventQueryCommandActions.setFavoriteEventBtnIds({
          favBtnIds: this.lockedCategoryId,
          matchAllCategories: true,
        })
      );

      this.updateUrl();
      return;
    }

    this.selectedCategoryIds = [];
    this.matchAllCategories = false;
    this.isEditorChoice = false;
    this.isNoneMode = false;
    this.pendingCategoryIds = [];
    this.pendingMatchAll = false;
    this.categoryTitle = 'EVENT CATEGORIES';

    this._store.dispatch(
      EventQueryCommandActions.setFavoriteEventBtnIds({
        favBtnIds: '',
        matchAllCategories: false,
      })
    );

    if (this.originLink) {
      this._location.go(this.originLink);
    }
  }

  private updateTitle() {
    if (this.isEditorChoice && this.selectedCategoryIds.length === 0) {
      this.categoryTitle = this.editorChoiceText;
      return;
    }
    if (this.lockedCategoryId) {
      const base = this.lockedCategory?.btnText?.trim() || 'Filtered';
      const extra = this.selectedCategoryIds.length;
      this.categoryTitle = extra > 0 ? `${base} +${extra}` : base;
      return;
    }
    if (this.selectedCategoryIds.length === 0) {
      this.categoryTitle = 'EVENT CATEGORIES';
    } else if (
      this.selectableCategories.length > 0 &&
      this.selectedCategoryIds.length === this.selectableCategories.length
    ) {
      this.categoryTitle = 'ALL CATEGORIES';
    } else if (this.selectedCategoryIds.length === 1) {
      const btn = this.customEventCategories.find(
        (c) => c._id === this.selectedCategoryIds[0]
      );
      this.categoryTitle = btn?.btnText || '1 SELECTED';
    } else {
      this.categoryTitle = `${this.selectedCategoryIds.length} SELECTED`;
    }
  }

  private updateUrl() {
    const idSource = this.lockedCategoryId
      ? [this.lockedCategoryId, ...this.selectedCategoryIds]
      : this.selectedCategoryIds;

    if (idSource.length === 0) {
      if (this.originLink) {
        this._location.go(this.originLink);
      }
      return;
    }

    const idsParam = idSource
      .map((id) => {
        const btn = this.customEventCategories.find((c) => c._id === id);
        return btn
          ? `${this.visitorService.convertToUrlSafeString(btn.btnText.trim())}-${id}`
          : id;
      })
      .join(',');

    let matchAllParam = '';
    if (this.matchAllCategories) {
      matchAllParam = '&ig-match-all=true';
    }

    const currentPath = this._location.path();
    if (currentPath.includes('ig-custom-events=')) {
      let basePath = currentPath.split('ig-custom-events=')[0];
      if (basePath.endsWith('?') || basePath.endsWith('&')) {
        basePath = basePath.slice(0, -1);
      }
      // Remove existing ig-match-all param
      basePath = basePath.replace(/[?&]ig-match-all=[^&]*/g, '');
      const connector = basePath.includes('?') ? '&' : '?';
      this._location.go(
        basePath + `${connector}ig-custom-events=${idsParam}${matchAllParam}`
      );
    } else {
      const connector = currentPath.includes('?') ? '&' : '?';
      this._location.go(
        currentPath + `${connector}ig-custom-events=${idsParam}${matchAllParam}`
      );
    }
  }
}
